import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSpoolNcrList } from '../../../../../../../redux/Spoolsbase/actions';
import { filterNcrRowsByWeldId, type NcrRow } from '../../spoolNcrApi';
import { WorkflowNcrSummaryTable } from '../weld/WorkflowNcrSummaryTable';
import { Button } from '../../../../../../../components/ui/button';
import type { SpoolCard, WeldJoint } from '../../SpoolsProduction';
import { Signature } from '../../Signature';
import type { SignaturePanelProps } from '../weld/signatureTypes';
import {
  StepInfoBanner,
  TriStateCheckRow,
  type TriStateCheck,
} from '../weld/spoolDetailFormUi';
import {
  buildQcSignaturePayload,
  triStateToApi,
  type SpoolStepPanelContext,
} from '../weld/spoolStepPayloadHelpers';
import { getWeldStepPanelPayload, weldPanelsRevision } from '../weld/weldStepPanelHydration';
import { Card } from '../../../../../../../components/ui/card';
import { CardContent } from '../../../../../../../components/ui/card';

type AcceptCheckKey = 'fitup' | 'preheat' | 'welding' | 'vt' | 'ndt' | 'coating' | 'trace';
type AcceptPanelSubmitMode = 'draft' | 'accept_complete';

const ACCEPT_CHECK_KEYS: AcceptCheckKey[] = [
  'fitup',
  'preheat',
  'welding',
  'vt',
  'ndt',
  'coating',
  'trace',
];

export type AcceptPanelSavePayload = {
  /** `draft` = in-progress; does not require FAT-complete or advance the workflow tab. */
  mode: 'draft' | 'accept_complete';
  projectId?: number | string | null;
  pipeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId?: string | null;
  weldNumber?: string | null;
  checks: Record<AcceptCheckKey, 'pass' | 'fail' | 'pending'>;
  signature: ReturnType<typeof buildQcSignaturePayload>;
  /** All accept tri-states in the form are pass (weld line only). */
  allComplete: boolean;
  spoolStatus: string;
};

const NEUTRAL_CHECKS: Record<AcceptCheckKey, TriStateCheck> = {
  fitup: 'neutral',
  preheat: 'neutral',
  welding: 'neutral',
  vt: 'neutral',
  ndt: 'neutral',
  coating: 'neutral',
  trace: 'neutral',
};

export interface AcceptPanelProps {
  spool: SpoolCard;
  /** Active joint — all accept state and saves are scoped to `weld.weldStepPanels.accept` (same as coating). */
  weld?: WeldJoint | null;
  sig: SignaturePanelProps;
  context?: SpoolStepPanelContext;
  onAcceptSave?: (payload: AcceptPanelSavePayload) => void | Promise<void>;
  onSubmittedNcrClick?: (row: NcrRow) => void;
  onOpenRaiseNcr?: () => void;
  /** Appends a full repair cycle after Accept (Fit-Up n … Accept n). Production Accept only. */
  onAddRepairCycle?: () => void;
  /** Set when this is Accept n inside a repair cycle. */
  repairNo?: number | null;
}

export function AcceptPanel({
  spool,
  weld,
  sig,
  context,
  onAcceptSave,
  onSubmittedNcrClick,
  onOpenRaiseNcr,
  onAddRepairCycle,
  repairNo = null,
}: AcceptPanelProps) {
  const dispatch = useDispatch();
  const ncrListAll = useSelector((s: { spoolsbase?: { ncrList?: NcrRow[] } }) => s.spoolsbase?.ncrList ?? []);
  const ncrList = useMemo(
    () =>
      filterNcrRowsByWeldId(ncrListAll, weld?.id ?? context?.weldId ?? null, {
        matchStepType: 'accept',
      }),
    [ncrListAll, weld?.id, context?.weldId]
  );
  const ncrListLoading = useSelector(
    (s: { spoolsbase?: { ncrListLoading?: boolean } }) => s.spoolsbase?.ncrListLoading ?? false
  );
  const spoolClearedForFat = spool.status === 'fat_complete';
  const [activeSubmit, setActiveSubmit] = useState<AcceptPanelSubmitMode | null>(null);
  const activeSubmitRef = useRef<AcceptPanelSubmitMode | null>(null);
  const acceptRemoteSaving = useSelector(
    (s: { spoolsbase?: { isSaving?: boolean; savingStep?: string | null } }) =>
      Boolean(s.spoolsbase?.isSaving && s.spoolsbase?.savingStep === 'accept')
  );

  useEffect(() => {
    const pid = context?.projectId;
    const poid = context?.pipeId;
    if (pid == null || pid === '' || poid == null || poid === '') return;
    void dispatch(
      fetchSpoolNcrList({ projectId: pid, pipeId: poid, spoolId: context?.spoolId ?? undefined })
    );
  }, [context?.projectId, context?.pipeId, context?.spoolId, dispatch]);

  const [acceptChecks, setAcceptChecks] = useState<Record<AcceptCheckKey, TriStateCheck>>(() => ({ ...NEUTRAL_CHECKS }));

  /**
   * Per-weld only: reset when there is no joint, then overlay `weldStepPanels.accept.payload` after draft/refetch or row change (see CoatingPanel).
   */
  useEffect(() => {
    if (!weld) {
      setAcceptChecks({ ...NEUTRAL_CHECKS });
      return;
    }

    const pl = getWeldStepPanelPayload(weld, 'accept') ?? {};
    if (pl.checks != null && typeof pl.checks === 'object' && !Array.isArray(pl.checks)) {
      const c = pl.checks as Record<string, string>;
      const toTri = (k: AcceptCheckKey): TriStateCheck => {
        const x = c[k];
        if (x === 'pass') return 'pass';
        if (x === 'fail') return 'fail';
        return 'neutral';
      };
      setAcceptChecks({
        fitup: toTri('fitup'),
        preheat: toTri('preheat'),
        welding: toTri('welding'),
        vt: toTri('vt'),
        ndt: toTri('ndt'),
        coating: toTri('coating'),
        trace: toTri('trace'),
      });
      return;
    }
    setAcceptChecks({ ...NEUTRAL_CHECKS });
  }, [weld?.id, weld?.status, weldPanelsRevision(weld ?? null)]);

  const cycleAccept = (key: AcceptCheckKey) => {
    setAcceptChecks((prev) => {
      const cur = prev[key];
      const next: TriStateCheck =
        cur === 'neutral' ? 'pass' : cur === 'pass' ? 'fail' : 'neutral';
      return { ...prev, [key]: next };
    });
  };

  const hasQcSignature = Boolean(sig.selectedQcId) || Boolean(sig.qcSignatureData);
  const allAcceptStepsPass = useMemo(
    () => ACCEPT_CHECK_KEYS.every((k) => acceptChecks[k] === 'pass'),
    [acceptChecks]
  );

  const buildPayload = useCallback(
    (mode: 'draft' | 'accept_complete'): AcceptPanelSavePayload => {
      const checks = ACCEPT_CHECK_KEYS.reduce(
        (acc, k) => {
          acc[k] = triStateToApi(acceptChecks[k]);
          return acc;
        },
        {} as Record<AcceptCheckKey, 'pass' | 'fail' | 'pending'>
      );
      return {
        mode,
        projectId: context?.projectId ?? null,
        pipeId: context?.pipeId ?? null,
        spoolId: context?.spoolId ?? spool.id,
        spoolName: context?.spoolName ?? spool.name,
        weldId: (context?.weldId ?? weld?.id) ?? null,
        weldNumber: (context?.weldNumber ?? weld?.number) ?? null,
        checks,
        signature: buildQcSignaturePayload(sig),
        /** True when every accept row in the form is passed (independent of spool FAT status). */
        allComplete: allAcceptStepsPass,
        spoolStatus: spool.status,
      };
    },
    [acceptChecks, allAcceptStepsPass, context, sig, spool.id, spool.name, spool.status, weld?.id, weld?.number]
  );

  const handleAcceptComplete = () => {
    if (!onAcceptSave || !weld) return;
    if (!hasQcSignature) return;
    activeSubmitRef.current = 'accept_complete';
    setActiveSubmit('accept_complete');
    void onAcceptSave(buildPayload('accept_complete'));
  };

  const handleSaveDraft = () => {
    if (!onAcceptSave || !weld) return;
    activeSubmitRef.current = 'draft';
    setActiveSubmit('draft');
    void onAcceptSave(buildPayload('draft'));
  };

  useEffect(() => {
    if (!acceptRemoteSaving) {
      activeSubmitRef.current = null;
      setActiveSubmit(null);
    }
  }, [acceptRemoteSaving]);

  const kind = activeSubmit ?? activeSubmitRef.current;
  const savingDraft = acceptRemoteSaving && kind === 'draft';
  const savingComplete = acceptRemoteSaving && kind === 'accept_complete';
  const canSave = Boolean(weld) && Boolean(onAcceptSave);

  const hasRepairableFailure = useMemo(
    () =>
      (['fitup', 'preheat', 'welding', 'vt', 'ndt', 'coating'] as AcceptCheckKey[]).some(
        (k) => acceptChecks[k] === 'fail'
      ),
    [acceptChecks]
  );

  const isRepairCycleAccept = repairNo != null && !Number.isNaN(Number(repairNo));

  return (
    <div className="grid min-h-0 w-full grid-cols-1 gap-4">
    <Card className="flex h-[min(75vh,720px)] min-h-0 flex-col overflow-hidden border border-gray-200 shadow-sm">
    <CardContent className="flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4">
        {ncrList.length > 0 && onSubmittedNcrClick && context?.projectId != null && context?.projectId !== '' && context?.pipeId != null && context?.pipeId !== '' ? (
          <WorkflowNcrSummaryTable
            ncrList={ncrList}
            loading={ncrListLoading}
            step="accept"
            stepTitle="Accept"
            weldId={context?.weldId ?? null}
            onRowClick={onSubmittedNcrClick}
          />
        ) : null}

      {isRepairCycleAccept ? (
        <StepInfoBanner title={`Accept ${Number(repairNo)}`} variant="notice">
          Repair cycle #{Number(repairNo)} — complete acceptance checks for this cycle.
        </StepInfoBanner>
      ) : null}

      <div className="space-y-2">
        <TriStateCheckRow
          tag="Accept"
          labels={{
            neutral: 'Fit-up check — pending',
            pass: 'Fit-up check — PASSED',
            fail: 'Fit-up check — FAILED',
          }}
          state={acceptChecks.fitup}
          onCycle={() => cycleAccept('fitup')}
        />
        <TriStateCheckRow
          tag="Accept"
          labels={{
            neutral: 'Preheat verification — pending',
            pass: 'Preheat verification — PASSED',
            fail: 'Preheat verification — FAILED',
          }}
          state={acceptChecks.preheat}
          onCycle={() => cycleAccept('preheat')}
        />
        <TriStateCheckRow
          tag="Accept"
          labels={{
            neutral: 'Welding pass verification — pending',
            pass: 'Welding pass verification — PASSED',
            fail: 'Welding pass verification — FAILED',
          }}
          state={acceptChecks.welding}
          onCycle={() => cycleAccept('welding')}
        />
        <TriStateCheckRow
          tag="Accept"
          labels={{
            neutral: 'Visual testing (VT) — pending',
            pass: 'Visual testing (VT) — PASSED',
            fail: 'Visual testing (VT) — FAILED',
          }}
          state={acceptChecks.vt}
          onCycle={() => cycleAccept('vt')}
        />
        <TriStateCheckRow
          tag="Accept"
          labels={{
            neutral: 'NDT — pending ',
            pass: 'NDT — PASSED ',
            fail: 'NDT — FAILED ',
          }}
          state={acceptChecks.ndt}
          onCycle={() => cycleAccept('ndt')}
        />
        <TriStateCheckRow
          tag="Accept"
          labels={{
            neutral: 'Field joint coating — pending',
            pass: 'Field joint coating — PASSED (DFT within spec, holiday test clear)',
            fail: 'Field joint coating — FAILED',
          }}
          state={acceptChecks.coating}
          onCycle={() => cycleAccept('coating')}
        />
        <TriStateCheckRow
          tag="Accept"
          labels={{
            neutral: 'Material traceability record — pending',
            pass: 'Material traceability record complete',
            fail: 'Material traceability record — incomplete',
          }}
          state={acceptChecks.trace}
          onCycle={() => cycleAccept('trace')}
        />
      </div>

      <Signature
        customerSignature={null}
        qcSignature={sig.selectedQcId}
        customerRepresentativeList={[]}
        qcList={sig.qcList}
        handleQcSignatureChange={sig.onQcChange}
        signatureData={sig.qcSignatureData}
        onSignatureChange={sig.onQcSignatureChange}
        showCustomerRep={false}
        showQc={true}
        qcLabel="QC Inspector"
        canSign={true}
        canEdit={true}
        isEditMode={true}
        isAdminOrSuperAdmin={true}
      />

      {spoolClearedForFat && (
        <StepInfoBanner title="FAT status" variant="success">
          ✓ All weld joints accepted — Spool cleared for FAT
        </StepInfoBanner>
      )}
      <div className="flex justify-end gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="border-red-300 text-red-600 hover:bg-red-50"
          disabled={!canSave || savingDraft || savingComplete}
          onClick={() => onOpenRaiseNcr?.()}
        >
          Fail — Raise NCR
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          disabled={!canSave || savingComplete}
          loading={savingDraft}
          loadingText="Saving draft…"
          onClick={handleSaveDraft}
        >
          Save Draft
        </Button>
        {!isRepairCycleAccept && onAddRepairCycle && hasRepairableFailure ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-50"
            disabled={!canSave || savingDraft || savingComplete}
            onClick={() => onAddRepairCycle()}
          >
            Add Repair Cycle
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          className="bg-hero-red text-white hover:bg-hero-red-60"
          disabled={!canSave || !hasQcSignature || savingDraft}
          loading={savingComplete}
          loadingText="Accepting…"
          onClick={handleAcceptComplete}
        >
          Accept Weld — Mark Complete
        </Button>
      </div>
      </CardContent>
    </Card>
    </div>
  );
}
